import React from 'react';
import PropTypes from 'prop-types';
import { CustomTooltip } from '../../Poppers';
import { DomUtils } from '../../../utils/dom-utils';
import * as S from './styled';
import { ButtonSizes, ButtonShapes } from './button-consts';

function checkAndSetDefaults(obj) {
  const defaultSize = S.sizes[ButtonSizes.SMALL];

  const width = obj.width !== undefined ? obj.width : defaultSize.width;
  const height = obj.height !== undefined ? obj.height : defaultSize.height;

  return { width, height };
}

export const Button = ({
  shape = ButtonShapes.FILLED,
  size = ButtonSizes.SMALL,
  onClick,
  children,
  disabled,
  icon,
  tooltip,
  loading,
  className,
  testId
}) => {
  const { elementRef, theme } = DomUtils.useDetectParentTheme();

  let _sizes = ButtonSizes.SMALL;
  if (typeof size === 'object') {
    _sizes = checkAndSetDefaults(size);
  } else if (typeof size === 'string') {
    _sizes = S.sizes[size];
  }

  const content = (
    <S.BasicButton
      ref={tooltip ? elementRef : null}
      $shape={shape}
      $loading={loading}
      $size={size}
      $buttonSize={_sizes}
      onClick={onClick}
      disabled={disabled}
      className={className}
      data-testid={testId}>
      {icon ? (
        <S.IconWrapper className="icon-wrapper">
          {icon}
          <S.Title $size={size}>{children}</S.Title>
        </S.IconWrapper>
      ) : (
        <S.TitleWrapper>
          <S.Title $size={size}>{children}</S.Title>
        </S.TitleWrapper>
      )}
      {loading ? <S.InProgress size={16} rotate color="var(--button-loading-icon-color)" /> : null}
    </S.BasicButton>
  );

  return tooltip ? (
    <CustomTooltip title={tooltip} mode={theme}>
      {content}
    </CustomTooltip>
  ) : (
    content
  );
};
Button.propTypes = {
  shape: PropTypes.oneOf(['FILLED', 'OUTLINED', 'LINKED', 'NAKED', 'NAKED_LIGHT']),
  size: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.oneOf(['SMALL', 'LARGE', 'XLARGE'])]),
  onClick: PropTypes.func,
  children: PropTypes.any,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  tooltip: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string
};

export const Filled = ({ size, onClick, children, disabled, icon, tooltip, loading, className, testId }) => (
  <Button
    shape={ButtonShapes.FILLED}
    size={size}
    onClick={onClick}
    disabled={disabled}
    icon={icon}
    tooltip={tooltip}
    loading={loading}
    className={className}
    testId={testId}>
    {children}
  </Button>
);
Filled.propTypes = {
  size: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.oneOf(['SMALL', 'LARGE', 'XLARGE'])]),
  onClick: PropTypes.func,
  children: PropTypes.any,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  tooltip: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string
};
Button.Filled = Filled;

export const Outlined = ({ size, onClick, children, disabled, icon, tooltip, loading, className, testId }) => (
  <Button
    shape={ButtonShapes.OUTLINED}
    size={size}
    onClick={onClick}
    disabled={disabled}
    icon={icon}
    tooltip={tooltip}
    loading={loading}
    className={className}
    testId={testId}>
    {children}
  </Button>
);
Outlined.propTypes = {
  size: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.oneOf(['SMALL', 'LARGE', 'XLARGE'])]),
  onClick: PropTypes.func,
  children: PropTypes.any,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  tooltip: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string
};
Button.Outlined = Outlined;

export const Linked = ({ onClick, children, disabled, icon, tooltip, loading, className, testId }) => (
  <Button
    shape={ButtonShapes.LINKED}
    size={ButtonSizes.SMALL}
    onClick={onClick}
    disabled={disabled}
    icon={icon}
    tooltip={tooltip}
    loading={loading}
    className={className}
    testId={testId}>
    {children}
  </Button>
);
Linked.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.any,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  tooltip: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string
};
Button.Linked = Linked;

export const Naked = ({ onClick, children, disabled, icon, tooltip, loading, className, testId }) => (
  <Button
    shape={ButtonShapes.NAKED}
    size={ButtonSizes.SMALL}
    onClick={onClick}
    disabled={disabled}
    icon={icon}
    tooltip={tooltip}
    loading={loading}
    className={className}
    testId={testId}>
    {children}
  </Button>
);
Naked.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.any,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  tooltip: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string
};
Button.Naked = Naked;

export const NakedLight = ({ onClick, children, disabled, icon, tooltip, loading, className, testId }) => (
  <Button
    shape={ButtonShapes.NAKED_LIGHT}
    size={ButtonSizes.SMALL}
    onClick={onClick}
    disabled={disabled}
    icon={icon}
    tooltip={tooltip}
    loading={loading}
    className={className}
    testId={testId}>
    {children}
  </Button>
);
NakedLight.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.any,
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  tooltip: PropTypes.node,
  loading: PropTypes.bool,
  className: PropTypes.string,
  testId: PropTypes.string
};
Button.NakedLight = NakedLight;
